import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

//#region lazy
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import { LiveStreamWidget } from "@state/stores/live-widgets/LiveStreamWidget";
import { getCurrentCulture } from "@utils";
import {
	isCapacitorPlatform,
	setCapacitorDeviceOrientation,
} from "@utils/specific/capacitor/helpers";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { VideoPlayerConfig, isOverlayMode } from "../config/LiveStreamConfig";
import { LiveStreamActionButtons } from "./LiveStreamActionButtons";
import { LiveStreamFullScreenOverlay } from "./LiveStreamFullScreenOverlay";
import { enterNativeFullscreen, useVideoElement } from "./useVideoElement";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
const ReactPlayer = lazy(loadFailPath, () => import("react-player"));

//#endregion lazy

export const VideoPlayer = observer(function VideoPlayer(props: {
	url: string;
	config: VideoPlayerConfig;
	widget: LiveStreamWidget;
}) {
	const [liveStreamStatus, setLiveStreamStatus] = useState<boolean>(true);
	const [pausedStatus, setPausedState] = useState<boolean>(
		!props.widget.autoPlay
	);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [muted, setMuted] = useState<boolean>(
		props.config.isMuteOnShowEnabled
	);

	const store = useLiveWidgetStore();
	const playerId = `ds-${props.widget.eventId}-${props.widget.location}`;
	const getVideo = useVideoElement(playerId);
	const t = useTranslation().t;

	// Capacitor orientation: attach to document fullscreenchange
	useEffect(() => {
		if (!isCapacitorPlatform()) return;

		const handleFullscreenChange = () => {
			if (document.fullscreenElement) {
				setCapacitorDeviceOrientation("landscape");
			} else {
				setCapacitorDeviceOrientation("portrait");
			}
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);

		return () => {
			document.removeEventListener(
				"fullscreenchange",
				handleFullscreenChange
			);
			setCapacitorDeviceOrientation("portrait");
		};
	}, []);

	let controlSettings = [];
	if (!props.config.allowFullScreen || props.config.isTablet) {
		controlSettings.push("nofullscreen");
	}
	if (!props.config.allowPlayBack) {
		controlSettings.push("noplaybackrate");
	}

	const onError = (
		error: any,
		data?: any,
		hlsInstance?: any,
		hlsGlobal?: any
	): void => {
		console.error(
			"Error",
			error,
			"Data: ",
			data,
			"HlsInstance: ",
			hlsInstance,
			"HlsGlobal",
			hlsGlobal
		);
		setCapacitorDeviceOrientation("portrait");
	};

	const onEnd = () => {
		store.updateWidget({
			...props.widget,
			message: {
				infoMessage: undefined,
				errorMessage: t<string>(
					"LIVE_WIDGETS.LIVE_STREAM.STREAM_ENDED"
				),
			},
		});
		setCapacitorDeviceOrientation("portrait");
	};

	const toggleMute = () => {
		const video = getVideo();
		if (video) {
			video.muted = !video.muted;
			setMuted(video.muted);
		}
	};

	const videoAttributes: Record<string, any> = {
		controlsList: controlSettings.join(" "),
	};
	if (!props.config.pip) {
		videoAttributes.disablePictureInPicture = true;
	}

	const innerContent = (
		<>
			<ReactPlayer
				url={props.url}
				controls={props.config.areControlsEnabled}
				config={{
					file: {
						attributes: videoAttributes,
						hlsVersion: "1.3.4",
					},
				}}
				playsinline={true}
				playing={!pausedStatus}
				width={props.config.playerWidth}
				height={props.config.playerHeight}
				style={props.config.playerInlineStyle}
				muted={muted}
				onPause={() => setPausedState(true)}
				onPlay={() => setPausedState(false)}
				id={playerId}
				className="widget--stream__player"
				onStart={() => {
					setLiveStreamStatus(true);
					const video = getVideo();
					if (video) {
						const syncMuted = () => setMuted(video.muted);
						video.addEventListener("fullscreenchange", syncMuted);
						video.addEventListener(
							"webkitendfullscreen",
							syncMuted
						);

						const pauseOnExit = () => {
							video.pause();
							setPausedState(true);
						};
						// Android: document fullscreenchange fires on enter + exit; pause only on exit
						document.addEventListener("fullscreenchange", () => {
							if (!document.fullscreenElement) pauseOnExit();
						});
						// iOS: native player exit
						video.addEventListener(
							"webkitendfullscreen",
							pauseOnExit
						);
					}
					if (props.widget.autoPlay) {
						runInAction(() => {
							props.widget.autoPlay = false;
						});
						if (isOverlayMode()) {
							setIsExpanded(true);
						} else {
							enterNativeFullscreen(video);
						}
					}
				}}
				onError={onError}
				onEnded={onEnd}
				data-allow-fullscreen="true"
			/>
			{liveStreamStatus && (
				<LiveStreamActionButtons
					playerId={playerId}
					pausedStatus={pausedStatus}
					setPausedState={setPausedState}
					onExpand={() => setIsExpanded(true)}
					isOverlayOpen={isExpanded}
					muted={muted}
					toggleMute={toggleMute}
				/>
			)}
		</>
	);

	const handleOverlayClose = () => {
		getVideo()?.pause();
		setPausedState(true);
		setIsExpanded(false);
	};

	return (
		<div
			className="widget--stream__inner"
			style={{ maxWidth: props.config.maxWidth || "" }}
		>
			<LiveStreamFullScreenOverlay
				open={isExpanded}
				onClose={handleOverlayClose}
			>
				{innerContent}
			</LiveStreamFullScreenOverlay>
		</div>
	);
});
